/**
 * App shell — the router (doc 147 §5.1).
 *
 * Presentational: reads the app store, dispatches through an injected
 * {@link AppActions} bag. `main.tsx` wires the Firebase-backed actions;
 * tests pass mocks. The side-effect wiring (auth watch, manifest subscription,
 * hash router, ⌘K keybinding) lives in `bootstrap.ts`.
 */
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { useAppStore, type InternalUser, type ViewMode } from '../state/store.js';
import type { Route } from '../state/hashRouter.js';
import type { TrailEntry } from '../state/workflowTrail.js';
import { useIsNarrowViewport } from '../state/useIsNarrowViewport.js';
import { useFocusTrap } from '../state/useFocusTrap.js';
import type { DensityMode } from '../theme/cssVariables.js';
import type { GraphContrast } from '../theme/tokens.js';
import type { ModeSetting } from '../state/appearance.js';
import type { LayoutCoords } from '../graph/buildGraph.js';
import { useSearch } from '../search/useSearch.js';
import { createSearchClient, type SearchClient } from '../search/searchClient.js';
import { Spinner } from '../components/Spinner.js';
import { EmptyState } from '../components/EmptyState.js';
import { SignInView } from './SignInView.js';
import { SessionExpiredBanner } from './SessionExpiredBanner.js';
import { TopBar } from './TopBar.js';
import { canAccessRoute } from '../state/routeAccess.js';
import { AccessDenied } from './AccessDenied.js';
import { DomainTree } from './DomainTree.js';
import { WelcomeView } from './WelcomeView.js';
import { EntityDetailView } from './EntityDetailView.js';
import { DomainView } from './DomainView.js';
import { HubView } from './HubView.js';
import { SurfaceDetailView } from './SurfaceDetailView.js';
import { CloudFunctionDetailView } from './CloudFunctionDetailView.js';
import { EventTypeDetailView } from './EventTypeDetailView.js';
import { FirestorePathDetailView } from './FirestorePathDetailView.js';
import { FeatureDetailView } from './FeatureDetailView.js';
import { FlagDetailView } from './FlagDetailView.js';
import { SettingsView } from './SettingsView.js';
import { SearchPalette } from './SearchPalette.js';
import { ConstellationView } from './ConstellationView.js';
import { WorkspaceView } from './WorkspaceView.js';
import { ShortcutsOverlay } from './ShortcutsOverlay.js';
import { SandboxEditorView } from './SandboxEditorView.js';
import { LintDashboardView } from './LintDashboardView.js';
import { SimulationView } from './SimulationView.js';
import { ComplianceConeView } from './ComplianceConeView.js';
import { ReadWritePatternsView } from './ReadWritePatternsView.js';
import { RefactorProposalsView } from './RefactorProposalsView.js';
import { BehavioralWalkerView } from './BehavioralWalkerView.js';
import { HeatmapView } from './HeatmapView.js';
import { StoryboardView } from './StoryboardView.js';
import { DriftTimelineView } from './DriftTimelineView.js';
import { TestDataGeneratorView } from './TestDataGeneratorView.js';
import { ComplianceHQView } from './ComplianceHQView.js';
import { StateMachineView } from './StateMachineView.js';
import { ReviewView } from './ReviewView.js';
import { PublishView } from './PublishView.js';
import { NewDesignChangeView } from './NewDesignChangeView.js';
import { navigate, navigateToEntity, routeToHash, takeSeedProposal, takeSimulateSandboxId, getPreviousNonAskRoute } from '../state/hashRouter.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';
import {
  focusEntity,
  clearFocus,
  focusedEntityId,
  type WorkspaceMode,
} from '../state/workspaceMachine.js';
import { mapKey, type KeyContext } from '../state/keyboardMap.js';
// Lazy chunks — analysis views aren't on the welcome→workspace critical
// path, so push their primitives (EntityPicker, BlastBars, PathDiagram) out
// of the initial bundle. Both lazy-load on first nav to #impact or #trace.
const ImpactView = lazy(() =>
  import('./ImpactView.js').then((m) => ({ default: m.ImpactView })),
);
const TraceView = lazy(() =>
  import('./TraceView.js').then((m) => ({ default: m.TraceView })),
);
const DashboardView = lazy(() =>
  import('./DashboardView.js').then((m) => ({ default: m.DashboardView })),
);
const EventFlowView = lazy(() =>
  import('./EventFlowView.js').then((m) => ({ default: m.EventFlowView })),
);
const PiiTracerView = lazy(() =>
  import('./PiiTracerView.js').then((m) => ({ default: m.PiiTracerView })),
);
const LiveMonitorView = lazy(() =>
  import('./LiveMonitorView.js').then((m) => ({ default: m.LiveMonitorView })),
);
const DataLineageView = lazy(() =>
  import('./DataLineageView.js').then((m) => ({ default: m.DataLineageView })),
);
const DomainMapView = lazy(() =>
  import('./DomainMapView.js').then((m) => ({ default: m.DomainMapView })),
);
const UserJourneysView = lazy(() =>
  import('./UserJourneysView.js').then((m) => ({ default: m.UserJourneysView })),
);
const CatalogView = lazy(() =>
  import('./CatalogView.js').then((m) => ({ default: m.CatalogView })),
);
const CatalogDomainDetailView = lazy(() =>
  import('./CatalogDomainDetailView.js').then((m) => ({ default: m.CatalogDomainDetailView })),
);
const CatalogGraphView = lazy(() =>
  import('./CatalogGraphView.js').then((m) => ({ default: m.CatalogGraphView })),
);
const FeatureCatalogView = lazy(() =>
  import('./FeatureCatalogView.js').then((m) => ({ default: m.FeatureCatalogView })),
);
import { AskAtlasView } from './AskAtlasView.js';
import type { AskAtlasContext } from './askAtlasContext.js';
import { BoardsView } from './BoardsView.js';
import { VersionDiffView } from './VersionDiffView.js';
import { GuidedTour } from './GuidedTour.js';
import { hasSeenTour, markTourSeen, clampStep } from '../state/tour.js';
import type { Board, Sandbox, ManifestDiff, AskAtlasResult } from '../state/atlasApiTypes.js';
import type { AiSuggestOptions } from '../state/aiPrompts.js';
import { EntityCommandPalette } from './EntityCommandPalette.js';
import { createInsightIndex, type InsightIndex } from '../state/entityInsights.js';
import { useMemo } from 'react';
import { RouteErrorBoundary } from '../components/RendererErrorBoundary.js';
import { Breadcrumb } from './Breadcrumb.js';

export interface AppActions {
  signIn: () => Promise<void>;
  signOut: () => void;
  setUserRole: (uid: string, role: InternalUser['role']) => Promise<void>;
  setDensity: (mode: DensityMode) => void;
  /** Set the tri-state appearance mode (auto/dark/light). */
  setMode: (mode: ModeSetting) => void;
  /** Toggle the constellation contrast (standard/high). */
  setGraphContrast: (level: GraphContrast) => void;
  togglePin: (entityId: string) => void;
  openSearch: () => void;
  closeSearch: () => void;
  // Cross-view navigation state (P1-5)
  setActiveEntityId: (id: string | null) => void;
  setActiveDomainId: (id: string | null) => void;
  setActiveLayerFilter: (filter: string | null) => void;
  // Phase 4
  askAtlas: (question: string) => Promise<AskAtlasResult>;
  runDiff: (fromSha: string, toSha: string) => Promise<ManifestDiff>;
  saveBoard: (name: string, entityIds: string[]) => Promise<void>;
  deleteBoard: (boardId: string) => Promise<void>;
  saveSandbox: (sandboxId: string, name: string, ops: unknown[], createdFromCommit?: string) => Promise<void>;
  deleteSandbox: (sandboxId: string) => Promise<void>;
  submitSandboxForReview: (
    sandboxId: string,
  ) => Promise<{ ok: true; sandboxId: string } | { ok: false; error: string }>;
  publishSandbox: (
    sandboxId: string,
    ownerUid?: string,
  ) => Promise<
    | { ok: true; sandboxId: string; prUrl: string; prNumber: number; publishedAt: number }
    | { ok: false; error: string }
  >;
  generateClickablePrototype: (
    overlayManifest: AtlasManifest,
  ) => Promise<
    | { ok: true; documentCount: number; emulatorUrl: string }
    | { ok: false; error: string }
  >;
  toggleView: () => void;
}

function isInputElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.getAttribute('contenteditable') === 'true';
}

/**
 * Stable onAsk handler shared by all AI surfaces. Module-level so it's not
 * recreated on every render, preventing unnecessary callback churn in
 * useAiSuggest.
 */
function onAskAtlas(opts: AiSuggestOptions): Promise<AskAtlasResult> {
  return streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds);
}

export interface AppProps {
  actions: AppActions;
  /** Injectable for tests; production uses the worker-or-main-thread client. */
  searchClientFactory?: () => SearchClient;
}

interface CenterProps {
  route: Route;
  manifest: AtlasManifest | null;
  manifestStatus: string;
  manifestError: string | null;
  layout: LayoutCoords;
  user: InternalUser;
  density: DensityMode;
  mode: ModeSetting;
  resolvedMode: 'dark' | 'light';
  graphContrast: GraphContrast;
  view: ViewMode;
  pinned: string[];
  recents: string[];
  boards: Board[];
  sandboxes: Sandbox[];
  adminReviewQueue: Array<Sandbox & { ownerUid: string }>;
  actions: AppActions;
  activeEntityId: string | null;
  activeDomainId: string | null;
  activeLayerFilter: string | null;
}

/**
 * Derive the AskAtlasContext from the current route and manifest.
 * Explicit focus (set by EntityDetailView's own Ask Atlas button) always wins.
 * Falls back to deriving context from the previous non-ask route for screens
 * like CatalogView, DashboardView, or global TopBar Ask Atlas button.
 */
function deriveAskAtlasContext(route: Route, manifest: AtlasManifest | null): AskAtlasContext {
  // Explicit focus (set by EntityDetailView's own Ask Atlas button) always wins.
  if (route.kind === 'ask' && route.focus) {
    return { kind: 'entity', entityId: route.focus };
  }
  if (!manifest) return null;
  if (route.kind !== 'ask') return null;

  // No explicit focus: derive context from whatever screen the user was on
  // before opening Ask Atlas (via the global TopBar button, or any other
  // call site that doesn't pass its own focus).
  const prev = getPreviousNonAskRoute();
  if (!prev) return null;
  switch (prev.kind) {
    case 'entity':
      return { kind: 'entity', entityId: prev.id };
    case 'sandbox':
      return { kind: 'sandbox', ...(prev.sandboxId ? { sandboxId: prev.sandboxId } : {}) };
    case 'simulate':
      return { kind: 'simulate', ...(prev.focus ? { focusEntityId: prev.focus } : {}) };
    case 'catalog':
      return { kind: 'catalog', ...(prev.query ? { query: prev.query } : {}) };
    case 'catalog-domain':
      return { kind: 'catalog-domain', domainId: prev.domainId };
    case 'dashboard':
      return { kind: 'dashboard' };
    default:
      return null;
  }
}

function renderWorkspace(
  route: Extract<Route, { kind: 'workspace' }>,
  manifest: AtlasManifest,
  props: CenterProps,
): React.JSX.Element {
  const { actions } = props;
  return (
    <WorkspaceView
      manifest={manifest}
      layout={props.layout}
      mode={props.resolvedMode}
      graphContrast={props.graphContrast}
      workspaceMode={route.mode}
      pinned={props.pinned}
      view={props.view}
      onFocus={(id) => navigate({ kind: 'workspace', mode: focusEntity(route.mode, id) })}
      onClearFocus={() => navigate({ kind: 'workspace', mode: clearFocus(route.mode) })}
      onOpenSheet={() => {
        const id = focusedEntityId(route.mode);
        if (id) navigateToEntity(id);
      }}
      onPopOutSheet={() => {
        const id = focusedEntityId(route.mode);
        if (id) window.open(routeToHash({ kind: 'entity', id }), '_blank', 'noopener');
      }}
      onCopyLink={() => {
        navigator.clipboard
          ?.writeText(window.location.href)
          .catch((err: unknown) => console.debug('[atlas] clipboard denied:', err));
      }}
      onTogglePin={actions.togglePin}
      onToggleView={actions.toggleView}
    />
  );
}

function CenterView(props: CenterProps): React.JSX.Element {
  const { route, manifest, manifestStatus, manifestError, user, actions, adminReviewQueue } = props;

  // Check route-level RBAC before rendering
  if (!canAccessRoute(route.kind, user.role)) {
    return <AccessDenied />;
  }

  if (route.kind === 'settings') {
    return (
      <SettingsView
        user={user}
        density={props.density}
        mode={props.mode}
        graphContrast={props.graphContrast}
        onDensityChange={actions.setDensity}
        onModeChange={actions.setMode}
        onGraphContrastChange={actions.setGraphContrast}
        onSetUserRole={actions.setUserRole}
      />
    );
  }

  if (manifestStatus === 'loading' || manifestStatus === 'idle') {
    return <Spinner label="Loading the manifest" />;
  }
  if (!manifest) {
    return (
      <EmptyState
        icon="sync_problem"
        title="The manifest isn’t available"
        detail={
          manifestError ??
          'Build it with `npm run atlas:manifest`, then publish it via the publishManifest function.'
        }
      />
    );
  }

  switch (route.kind) {
    case 'entity':
      return (
        <EntityDetailView
          manifest={manifest}
          entityId={route.id}
          pinned={props.pinned}
          onTogglePin={actions.togglePin}
          onEntitySelected={actions.setActiveEntityId}
          {...(route.field ? { selectedField: route.field } : {})}
        />
      );
    case 'domain':
      return <DomainView manifest={manifest} domainId={route.id} />;
    case 'hub':
      return <HubView manifest={manifest} {...(route.id ? { hubId: route.id } : {})} />;
    case 'feature':
      return <FeatureDetailView manifest={manifest} featureId={route.id} />;
    case 'surface':
      return <SurfaceDetailView manifest={manifest} surfaceId={route.id} />;
    case 'cloud-function':
      return <CloudFunctionDetailView manifest={manifest} functionName={route.id} />;
    case 'event-type':
      return <EventTypeDetailView manifest={manifest} eventType={route.id} />;
    case 'firestore-path':
      return <FirestorePathDetailView manifest={manifest} entityId={route.entityId} />;
    case 'constellation':
      return (
        <ConstellationView
          manifest={manifest}
          layout={props.layout}
          mode={props.resolvedMode}
          graphContrast={props.graphContrast}
          route={route}
          activeEntityId={props.activeEntityId}
        />
      );
    case 'relationship-map':
      return (
        <Suspense fallback={<Spinner label="Loading relationship map" />}>
          <DomainMapView manifest={manifest} route={route} />
        </Suspense>
      );
    case 'workspace':
      return renderWorkspace(route, manifest, props);
    case 'impact':
      return (
        <Suspense fallback={<Spinner label="Loading impact analyzer" />}>
          <ImpactView manifest={manifest} entityId={route.id} onAsk={onAskAtlas} />
        </Suspense>
      );
    case 'trace':
      return (
        <Suspense fallback={<Spinner label="Loading path trace" />}>
          <TraceView manifest={manifest} from={route.from} to={route.to} onAsk={onAskAtlas} />
        </Suspense>
      );
    case 'ask': {
      const screenContext: AskAtlasContext = deriveAskAtlasContext(route, manifest);
      return <AskAtlasView onAsk={actions.askAtlas} screenContext={screenContext} />;
    }
    case 'boards':
      return (
        <BoardsView
          boards={props.boards}
          pinned={props.pinned}
          onSaveBoard={actions.saveBoard}
          onDeleteBoard={actions.deleteBoard}
        />
      );
    case 'diff':
      return <VersionDiffView onRunDiff={actions.runDiff} />;
    case 'lint':
      return (
        <LintDashboardView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'simulate':
      return (
        <SimulationView
          manifest={manifest}
          {...(route.focus ? { focus: route.focus } : {})}
          sandboxes={props.sandboxes}
          initialSandboxId={takeSimulateSandboxId()}
        />
      );
    case 'compliance':
      return (
        <ComplianceConeView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
          onAsk={onAskAtlas}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'patterns':
      return (
        <ReadWritePatternsView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'refactor':
      return (
        <RefactorProposalsView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
          onAsk={onAskAtlas}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'behavioral':
      return (
        <BehavioralWalkerView
          manifest={manifest}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'heatmap':
      return (
        <HeatmapView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'storyboard':
      return (
        <StoryboardView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'drift':
      return (
        <DriftTimelineView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
          {...(route.focus ? { focus: route.focus } : {})}
        />
      );
    case 'testdata':
      return (
        <TestDataGeneratorView
          manifest={manifest}
          {...(route.entityId ? { entityId: route.entityId } : {})}
        />
      );
    case 'compliance-hq':
      return (
        <ComplianceHQView
          manifest={manifest}
          onNavigateEntity={navigateToEntity}
        />
      );
    case 'statemachine':
      return <StateMachineView manifest={manifest} {...(route.machineId ? { machineId: route.machineId } : {})} />;
    case 'dashboard':
      return (
        <Suspense fallback={<Spinner label="Loading dashboard" />}>
          <DashboardView manifest={manifest} onNavigate={navigate} sandboxes={props.sandboxes} />
        </Suspense>
      );
    case 'event-flow':
      return (
        <Suspense fallback={<Spinner label="Loading event flow" />}>
          <EventFlowView manifest={manifest} />
        </Suspense>
      );
    case 'pii-tracer':
      return (
        <Suspense fallback={<Spinner label="Loading PII tracer" />}>
          <PiiTracerView
            manifest={manifest}
            {...(route.entityId ? { focusEntityId: route.entityId } : {})}
            onNavigateEntity={navigateToEntity}
          />
        </Suspense>
      );
    case 'live-monitor':
      return (
        <Suspense fallback={<Spinner label="Loading live monitor" />}>
          <LiveMonitorView manifest={manifest} />
        </Suspense>
      );
    case 'lineage':
      return (
        <Suspense fallback={<Spinner label="Loading data lineage" />}>
          <DataLineageView manifest={manifest} />
        </Suspense>
      );
    case 'domain-map':
      return (
        <Suspense fallback={<Spinner label="Loading domain map" />}>
          <DomainMapView manifest={manifest} route={route} />
        </Suspense>
      );
    case 'user-journeys':
      return (
        <Suspense fallback={<Spinner label="Loading user journeys" />}>
          <UserJourneysView manifest={manifest} route={route} />
        </Suspense>
      );
    case 'catalog':
      return (
        <Suspense fallback={<Spinner label="Loading catalog" />}>
          <CatalogView manifest={manifest} />
        </Suspense>
      );
    case 'catalog-domain':
      return (
        <Suspense fallback={<Spinner label="Loading domain details" />}>
          <CatalogDomainDetailView manifest={manifest} domainId={route.domainId} />
        </Suspense>
      );
    case 'catalog-graph':
      return (
        <Suspense fallback={<Spinner label="Loading graph" />}>
          <CatalogGraphView manifest={manifest} route={route} />
        </Suspense>
      );
    case 'flag-catalog':
      return (
        <Suspense fallback={<Spinner label="Loading flag catalog" />}>
          {route.flagId ? (
            <FlagDetailView manifest={manifest} flagId={route.flagId} />
          ) : (
            <FeatureCatalogView manifest={manifest} route={route} onAsk={onAskAtlas} />
          )}
        </Suspense>
      );
    case 'sandbox': {
      const seedProposal = takeSeedProposal() ?? undefined;
      return (
        <SandboxEditorView
          manifest={manifest}
          sandboxes={props.sandboxes}
          activeSandboxId={route.sandboxId}
          {...(route.seedEntity ? { seedEntity: route.seedEntity } : {})}
          {...(route.seedObject ? { seedObject: route.seedObject } : {})}
          seedProposal={seedProposal}
          onAsk={onAskAtlas}
          onSelectSandbox={(id) => navigate({ kind: 'sandbox', ...(id ? { sandboxId: id } : {}) })}
          onSaveSandbox={actions.saveSandbox}
          onDeleteSandbox={actions.deleteSandbox}
          onNavigateEntity={navigateToEntity}
          onSubmitForReview={actions.submitSandboxForReview}
          onNavigate={navigate}
        />
      );
    }
    case 'review': {
      const sandboxId = route.sandboxId;
      const ownerUid = route.ownerUid;
      // Try personal sandboxes first, then admin queue if ownerUid is present
      let sandbox = props.sandboxes.find((s) => s.id === sandboxId);
      if (!sandbox && ownerUid) {
        sandbox = adminReviewQueue.find((s) => s.id === sandboxId && s.ownerUid === ownerUid);
      }
      if (!sandbox) {
        return <EmptyState icon="help" title={`Sandbox ${sandboxId} not found.`} />;
      }
      return (
        <ReviewView
          manifest={manifest}
          sandbox={sandbox}
          onNavigateBack={() => navigate({ kind: 'sandbox', sandboxId })}
          onApproveAndPublish={() => navigate({ kind: 'publish', sandboxId, ...(ownerUid ? { ownerUid } : {}) })}
          onGeneratePrototype={actions.generateClickablePrototype}
          onSaveSandbox={actions.saveSandbox}
          isAdmin={user?.role === 'admin'}
          adminQueue={adminReviewQueue}
        />
      );
    }
    case 'publish': {
      const sandboxId = route.sandboxId;
      const ownerUid = route.ownerUid;
      // Try personal sandboxes first, then admin queue if ownerUid is present
      let sandbox = props.sandboxes.find((s) => s.id === sandboxId);
      if (!sandbox && ownerUid) {
        sandbox = adminReviewQueue.find((s) => s.id === sandboxId && s.ownerUid === ownerUid);
      }
      if (!sandbox) {
        return <EmptyState icon="help" title={`Sandbox ${sandboxId} not found.`} />;
      }
      return (
        <PublishView
          manifest={manifest}
          sandbox={sandbox}
          onNavigateBack={() => navigate({ kind: 'review', sandboxId, ...(ownerUid ? { ownerUid } : {}) })}
          onPublish={(sid: string) => actions.publishSandbox(sid, ownerUid)}
        />
      );
    }
    case 'new-design-change': {
      return (
        <NewDesignChangeView
          manifest={manifest}
          onSubmit={async (op) => {
            // Same id scheme as SandboxEditorView's newSandbox() helper.
            const newSandboxId = `sb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            await actions.saveSandbox(newSandboxId, `New ${op.kind.replace('create-', '')}`, [op], manifest.generatedFromCommit);
            navigate({ kind: 'sandbox', sandboxId: newSandboxId });
          }}
          onCancel={() => navigate({ kind: 'welcome' })}
          onAsk={onAskAtlas}
        />
      );
    }
    case 'welcome':
      return (
        <WelcomeView
          manifest={manifest}
          recents={props.recents}
          onOpenSearch={actions.openSearch}
        />
      );
  }
}

/**
 * Resolve the entity id the user is currently looking at, across the
 * shapes the router supports. Used by the entity command palette so the
 * `e` shortcut "just works" wherever the user is.
 */
function currentEntityId(state: ReturnType<typeof useAppStore>): string | null {
  const r = state.route;
  if (r.kind === 'impact' || r.kind === 'entity') return r.id;
  if (r.kind === 'workspace' && r.mode.kind === 'focused') {
    return r.mode.entityId;
  }
  return null;
}

/** Wraps the palette so the InsightIndex is built lazily, only on open. */
function EntityActionsHost({
  manifest,
  entityId,
  onClose,
}: {
  manifest: AtlasManifest;
  entityId: string;
  onClose: () => void;
}): React.JSX.Element {
  const index: InsightIndex = useMemo(() => createInsightIndex(manifest), [manifest]);
  return <EntityCommandPalette index={index} entityId={entityId} onClose={onClose} />;
}

/**
 * Wraps the global search palette so the InsightIndex powering the
 * "For <Entity>" context section is built lazily on open. When no entity
 * is focused, the index is omitted and the palette behaves like before.
 */
function SearchPaletteHost({
  search,
  ready,
  onClose,
  manifest,
  entityContext,
  trail,
  pinned,
}: {
  search: React.ComponentProps<typeof SearchPalette>['search'];
  ready: boolean;
  onClose: () => void;
  manifest: AtlasManifest | null;
  entityContext: string | undefined;
  trail?: readonly TrailEntry[];
  pinned?: readonly string[];
}): React.JSX.Element {
  const index: InsightIndex | undefined = useMemo(
    () => (manifest && entityContext ? createInsightIndex(manifest) : undefined),
    [manifest, entityContext],
  );
  return (
    <SearchPalette
      search={search}
      ready={ready}
      onClose={onClose}
      {...(entityContext ? { entityContext } : {})}
      {...(index ? { index } : {})}
      {...(trail ? { trail } : {})}
      {...(pinned ? { pinned } : {})}
      {...(manifest ? { manifest, onAsk: onAskAtlas } : {})}
    />
  );
}

export function App({
  actions,
  searchClientFactory = createSearchClient,
}: AppProps): React.JSX.Element {
  const state = useAppStore();
  const { search, ready } = useSearch(state.manifest, searchClientFactory);
  const [tourStep, setTourStep] = useState(0);
  const [tourOpen, setTourOpen] = useState(() => !hasSeenTour());
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [entityActionsOpen, setEntityActionsOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('atlas-sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isNarrowViewport = useIsNarrowViewport();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Trap focus within the mobile drawer when open
  useFocusTrap(drawerRef, mobileNavOpen && isNarrowViewport);

  // Workspace mode is encoded in the route — derive once for the dispatcher.
  const workspaceMode: WorkspaceMode =
    state.route.kind === 'workspace' ? state.route.mode : { kind: 'idle' };

  function handleToggleSidebar(): void {
    if (isNarrowViewport) {
      // On mobile: toggle the drawer open/closed
      setMobileNavOpen((prev) => !prev);
    } else {
      // On desktop: toggle the sidebar collapse state
      setSidebarCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem('atlas-sidebar-collapsed', String(next));
        } catch {
          /* ignore localStorage errors */
        }
        return next;
      });
    }
  }

  // Close mobile drawer when route changes (standard mobile UX)
  useEffect(() => {
    if (mobileNavOpen && isNarrowViewport) {
      setMobileNavOpen(false);
    }
  }, [state.route]);

  // Close mobile drawer on Escape key
  useEffect(() => {
    if (!mobileNavOpen) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mobileNavOpen]);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (mobileNavOpen && isNarrowViewport) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen, isNarrowViewport]);

  // Global keys only. Workspace-scoped keys (escape, enter, arrows, p, f,
  // 1–9, copy-*) are dispatched by WorkspaceView since they need the graph
  // controller and manifest. The two listeners act on disjoint action kinds.
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      const inputActive = isInputElement(document.activeElement);
      const ctx: KeyContext = { workspaceMode, inputActive };
      const action = mapKey(event, ctx);
      if (!action) return;
      if (action.kind === 'open-search') {
        event.preventDefault();
        actions.openSearch();
      } else if (action.kind === 'open-shortcuts') {
        event.preventDefault();
        setShortcutsOpen(true);
      } else if (action.kind === 'toggle-view-3d') {
        event.preventDefault();
        actions.toggleView();
      } else if (action.kind === 'open-entity-actions') {
        event.preventDefault();
        setEntityActionsOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [workspaceMode, actions]);

  if (state.authStatus === 'idle' || state.authStatus === 'loading') {
    return <Spinner label="Connecting to Atlas" />;
  }
  if (!state.user) {
    return <SignInView onSignIn={actions.signIn} />;
  }

  const showTree = state.manifest !== null && state.route.kind !== 'settings';
  const closeTour = (): void => {
    markTourSeen();
    setTourOpen(false);
  };

  const resolvedMode: 'dark' | 'light' =
    state.mode === 'dark' || state.mode === 'light'
      ? state.mode
      : state.systemPrefersDark
        ? 'dark'
        : 'light';

  return (
    <div className="app-shell">
      {state.sessionExpired && <SessionExpiredBanner onReauth={actions.signIn} />}
      <TopBar
        user={state.user}
        resolvedMode={resolvedMode}
        onOpenSearch={actions.openSearch}
        onSignOut={actions.signOut}
        onToggleMode={actions.setMode}
        sidebarCollapsed={isNarrowViewport ? !mobileNavOpen : sidebarCollapsed}
        onToggleSidebar={handleToggleSidebar}
      />
      <div className="app-body">
        {isNarrowViewport && mobileNavOpen && <div className="mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} />}
        {showTree && state.manifest && (
          <div ref={drawerRef}>
            <DomainTree
              manifest={state.manifest}
              route={state.route}
              pinned={state.pinned}
              collapsed={sidebarCollapsed}
              mobileOpen={mobileNavOpen && isNarrowViewport}
            />
          </div>
        )}
        <main className="center-pane">
          <Breadcrumb route={state.route} className="app-breadcrumb" />
          <RouteErrorBoundary
            fallback={
              <div className="route-crash">
                <strong>This view crashed.</strong>{' '}
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={() => location.reload()}
                >
                  Reload
                </button>
              </div>
            }
          >
            <CenterView
              route={state.route}
              manifest={state.manifest}
              manifestStatus={state.manifestStatus}
              manifestError={state.manifestError}
              layout={state.layout}
              user={state.user}
              density={state.density}
              mode={state.mode}
              resolvedMode={resolvedMode}
              graphContrast={state.graphContrast}
              pinned={state.pinned}
              recents={state.recents}
              boards={state.boards}
              sandboxes={state.sandboxes}
              adminReviewQueue={state.adminReviewQueue}
              view={state.view}
              actions={actions}
              activeEntityId={state.activeEntityId}
              activeDomainId={state.activeDomainId}
              activeLayerFilter={state.activeLayerFilter}
            />
          </RouteErrorBoundary>
        </main>
      </div>
      {state.searchOpen && (
        <RouteErrorBoundary fallback={null}>
          <SearchPaletteHost
            search={search}
            ready={ready}
            onClose={actions.closeSearch}
            manifest={state.manifest}
            entityContext={currentEntityId(state) ?? undefined}
            trail={state.entityTrail}
            pinned={state.pinned}
          />
        </RouteErrorBoundary>
      )}
      {shortcutsOpen && (
        <RouteErrorBoundary fallback={null}>
          <ShortcutsOverlay onClose={() => setShortcutsOpen(false)} />
        </RouteErrorBoundary>
      )}
      {entityActionsOpen && state.manifest && currentEntityId(state) && (
        <RouteErrorBoundary fallback={null}>
          <EntityActionsHost
            manifest={state.manifest}
            entityId={currentEntityId(state)!}
            onClose={() => setEntityActionsOpen(false)}
          />
        </RouteErrorBoundary>
      )}
      {tourOpen && (
        <RouteErrorBoundary fallback={null}>
          <GuidedTour
            stepIndex={tourStep}
            onNext={() => setTourStep((s) => clampStep(s + 1))}
            onPrev={() => setTourStep((s) => clampStep(s - 1))}
            onClose={closeTour}
            manifest={state.manifest}
          />
        </RouteErrorBoundary>
      )}
    </div>
  );
}
